import { PetsOutlined } from "@mui/icons-material";
import { Avatar, Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TbGrave2 } from "react-icons/tb";
import { HOT_PINK } from "../../../../../enums/Colors";
import { petViewUrl } from "../../../../../enums/UrlPaths";
import usePetAvatar from "../../../../../hooks/usePetAvatar";
import usePrimaryColor from "../../../../../hooks/usePrimaryColor";
import { trimText } from "../../../../../utils/Functions";
import { isFemaleGender } from "../../../../pets/utils/Functions";
import LinkCard, { getInitials } from "./LinkCard";
import { getAppointmentOwner } from "../../utils/getAppointmentOwner";

export default function PetCard({ appointment }) {
  const { t, i18n } = useTranslation();
  const pet = appointment?.pet;
  const species =
    pet?.type?.translations?.[i18n.language] || pet?.type?.title || "";
  const to = petViewUrl(pet?.id, getAppointmentOwner(appointment)?.id);

  return (
    <LinkCard
      icon={<PetsOutlined color="primary" />}
      title={t("PetInformation")}
      to={to}
      testId="appointment.details.pet-chip"
    >
      <PetAvatar pet={pet} />
      <Box minWidth={0}>
        <Typography variant="body1" fontWeight={600} noWrap>
          {trimText(pet?.name, 50)}
        </Typography>
        {species ? (
          <Typography variant="body2" color="primary" display="block">
            {species}
          </Typography>
        ) : null}
      </Box>
    </LinkCard>
  );
}

/** The pet's uploaded avatar, or its initials on a background colour-coded by gender. */
function PetAvatar({ pet }) {
  const primaryColor = usePrimaryColor();
  const { avatar, isLoading } = usePetAvatar(pet?.id);
  const showImage = Boolean(avatar) && !isLoading;

  return (
    <Avatar
      src={showImage ? avatar : undefined}
      alt={pet?.name}
      data-testid="appointment.details.pet-avatar"
      sx={{
        width: 36,
        height: 36,
        bgcolor: showImage ? undefined : getGenderColor(pet, primaryColor),
        color: "white",
      }}
    >
      {showImage ? null : pet?.deceased ? (
        <TbGrave2 size={20} />
      ) : (
        <Typography variant="body2" fontWeight={600}>
          {getInitials(pet?.name)}
        </Typography>
      )}
    </Avatar>
  );
}

function getGenderColor(pet, primaryColor) {
  if (pet?.deceased) return "gray";
  return isFemaleGender(pet?.gender) ? HOT_PINK : primaryColor;
}
